import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { PaymentMethod, RecurringExpenseDTO, RecurringIncomeDTO } from '@shared/types';

export interface CreateRecurringIncomeInput {
  categoryId?: number | null;
  amount: number;
  description: string;
  dayOfMonth: number;
  startMonth: string;
}

export interface CreateRecurringExpenseInput extends CreateRecurringIncomeInput {
  paymentMethod?: PaymentMethod;
}

export interface UpdateRecurringInput {
  active?: boolean;
  endMonth?: string | null;
}

function invalidateAfterRecurringChange(
  queryClient: ReturnType<typeof useQueryClient>,
  kind: 'incomes' | 'expenses',
): void {
  queryClient.invalidateQueries({ queryKey: ['recurring', kind] });
  queryClient.invalidateQueries({ queryKey: [kind] });
  queryClient.invalidateQueries({ queryKey: ['dashboard'] });
}

export function useRecurringIncomes() {
  return useQuery({
    queryKey: ['recurring', 'incomes'],
    queryFn: async () => (await api.get<RecurringIncomeDTO[]>('/recurring/incomes')).data,
  });
}

export function useCreateRecurringIncome() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateRecurringIncomeInput) =>
      (await api.post<RecurringIncomeDTO>('/recurring/incomes', input)).data,
    onSuccess: () => invalidateAfterRecurringChange(queryClient, 'incomes'),
  });
}

export function useUpdateRecurringIncome() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...input }: UpdateRecurringInput & { id: number }) =>
      (await api.patch<RecurringIncomeDTO>(`/recurring/incomes/${id}`, input)).data,
    onSuccess: () => invalidateAfterRecurringChange(queryClient, 'incomes'),
  });
}

export function useRecurringExpenses() {
  return useQuery({
    queryKey: ['recurring', 'expenses'],
    queryFn: async () => (await api.get<RecurringExpenseDTO[]>('/recurring/expenses')).data,
  });
}

export function useCreateRecurringExpense() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateRecurringExpenseInput) =>
      (await api.post<RecurringExpenseDTO>('/recurring/expenses', input)).data,
    onSuccess: () => invalidateAfterRecurringChange(queryClient, 'expenses'),
  });
}

export function useUpdateRecurringExpense() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...input }: UpdateRecurringInput & { id: number }) =>
      (await api.patch<RecurringExpenseDTO>(`/recurring/expenses/${id}`, input)).data,
    onSuccess: () => invalidateAfterRecurringChange(queryClient, 'expenses'),
  });
}
